// ============================================================================
// 📁 FILE: src/modules/ticket/components/detail/EditTicketDrawer.js
// 📆 Updated On: 2025-12-04  (generated)
// 📝 Summary:
// - Uses /ticket-management/create-support-data for all picklists
// - Replaces native <select> with GlassSelect (custom dropdown)
// - Sanitizes payload (empty -> null, numeric casting for id fields)
// - Searchable, keyboard-light GlassSelect with simple outside-click handling
// - Reusable: used for Priority, Severity, Ticket Type, Category, Customer
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

// axios client (relative path used in this repo)
import axiosClient from "../../../../api/axiosClient";

// icons
import { X as IconClose, ChevronDown } from "lucide-react";

// styles (assumes you created/used the editTicketDrawer.css)
import "./editTicketDrawer.css";

/* ============================================================================
   Helper: safeNumber
   - Convert to integer when possible; return null for empty/invalid
   ============================================================================ */
function safeNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  // if already a number
  if (typeof v === "number") return v;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

/* ============================================================================
   GlassSelect (internal reusable custom dropdown)
   - Props:
     options: [{ value, label }]
     value: current value (value type preserved)
     onChange: (value) => void
     placeholder: string
     searchable: bool
     disabled: bool
   - Lightweight but accessible: keyboard (Enter/Escape), outside click closes
   ============================================================================ */
function GlassSelect({
  options = [],
  value,
  onChange,
  placeholder = "-- Select --",
  searchable = false,
  disabled = false,
  id,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  // close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // filtered options
  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => String(o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  // label for current value
  const currentLabel = useMemo(() => {
    const found = options.find((o) => String(o.value) === String(value));
    return found ? found.label : "";
  }, [options, value]);

  function handleSelect(opt) {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  }

  function toggleOpen() {
    if (disabled) return;
    setOpen((s) => !s);
    setQuery("");
  }

  return (
    <div ref={rootRef} className="tf-glass-select" style={{ position: "relative" }}>
      <div
        className="tf-field-row"
        onClick={() => toggleOpen()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") toggleOpen();
          if (e.key === "Escape") setOpen(false);
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
        id={id}
      >
        <label style={{ display: "block" }}>{placeholder}</label>
        <div
          className="tf-select-view"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "10px 12px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.35)",
            background: "rgba(255,255,255,0.12)",
            color: "#fff",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          <div style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", flex: 1 }}>
            {currentLabel || <span style={{ opacity: 0.65 }}>{placeholder}</span>}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>

      {open && (
        <div
          className="tf-select-dropdown"
          style={{
            position: "absolute",
            right: 0,
            left: 0,
            marginTop: 8,
            zIndex: 4000,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 10,
            boxShadow: "0 8px 26px rgba(0,0,0,0.5)",
            maxHeight: 220,
            overflow: "auto",
            padding: 8,
          }}
          role="listbox"
        >
          {searchable && (
            <div style={{ padding: "6px 8px 10px 8px" }}>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "#fff",
                }}
              />
            </div>
          )}

          {filtered.length === 0 && (
            <div style={{ padding: "10px", color: "rgba(255,255,255,0.7)" }}>No options</div>
          )}

          {filtered.map((opt) => (
            <div
              key={String(opt.value)}
              role="option"
              aria-selected={String(opt.value) === String(value)}
              onClick={() => handleSelect(opt)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSelect(opt);
              }}
              tabIndex={0}
              style={{
                padding: "8px 10px",
                borderRadius: 8,
                marginBottom: 6,
                background: String(opt.value) === String(value) ? "linear-gradient(90deg,#8f2de2,#e43aab)" : "transparent",
                color: String(opt.value) === String(value) ? "#fff" : "#fff",
                cursor: "pointer",
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

GlassSelect.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

/* ============================================================================
   MAIN: EditTicketDrawer (uses GlassSelect)
   - Loads support data from /ticket-management/create-support-data when opened
   - Uses GlassSelect for all dropdowns: Priority, Severity, Ticket Type, Category, Customer
   - Sanitizes payload before PUT
   ============================================================================ */
export default function EditTicketDrawer({ isOpen, onClose, ticket, onUpdated }) {
  // local states
  const [loading, setLoading] = useState(false);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportData, setSupportData] = useState({
    ticket_types: [],
    categories: [],
    customers: [],
    sla_profiles: [],
    custom_fields: [],
  });

  const [form, setForm] = useState({
    title: "",
    description: "",
    priority: "",
    severity: "",
    ticket_type_id: null,
    category_id: null,
    customer_id: null,
    custom_fields: {},
  });

  // when drawer opens, populate form & load support data
  useEffect(() => {
    if (!isOpen || !ticket) return;

    setForm({
      title: ticket.title || "",
      description: ticket.description || "",
      priority: ticket.priority || "",
      severity: ticket.severity || "",
      // ticket.ticket_type may be object in GET response
      ticket_type_id: ticket.ticket_type?.id ?? ticket.ticket_type_id ?? null,
      category_id: ticket.category?.id ?? ticket.category_id ?? null,
      customer_id: ticket.reported_customer?.id ?? ticket.customer_id ?? null,
      custom_fields: ticket.custom_fields || {},
    });

    loadSupportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticket]);

  // fetch support data
  async function loadSupportData() {
    setSupportLoading(true);
    try {
      // call the single endpoint that returns all picklists (works with your backend)
      const resp = await axiosClient.get("/ticket-management/create-support-data");
      if (resp && resp.data && resp.data.success) {
        setSupportData({
          ticket_types: resp.data.data.ticket_types || [],
          categories: resp.data.data.categories || [],
          customers: resp.data.data.customers || [],
          sla_profiles: resp.data.data.sla_profiles || [],
          custom_fields: resp.data.data.custom_fields || [],
        });
      } else if (resp && resp.data) {
        // fallback if structure slightly differs
        setSupportData({
          ticket_types: resp.data.ticket_types || [],
          categories: resp.data.categories || [],
          customers: resp.data.customers || [],
          sla_profiles: resp.data.sla_profiles || [],
          custom_fields: resp.data.custom_fields || [],
        });
      }
    } catch (err) {
      console.error("Failed to load support data:", err);
    } finally {
      setSupportLoading(false);
    }
  }

  // update field helpers
  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateCustomField(key, value) {
    setForm((prev) => ({
      ...prev,
      custom_fields: { ...prev.custom_fields, [key]: value },
    }));
  }

  // build options helpers
  const priorityOptions = [
    { value: "", label: "-" },
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
    { value: "CRITICAL", label: "Critical" },
  ];

  const severityOptions = [
    { value: "", label: "-" },
    { value: "MINOR", label: "Minor" },
    { value: "MAJOR", label: "Major" },
    { value: "CRITICAL", label: "Critical" },
  ];

  const ticketTypeOptions = useMemo(() => {
    return [{ value: "", label: "-- Select --" }].concat(
      supportData.ticket_types.map((t) => ({ value: t.id, label: t.name }))
    );
  }, [supportData.ticket_types]);

  const categoryOptions = useMemo(() => {
    return [{ value: "", label: "-- Select --" }].concat(
      supportData.categories.map((c) => ({ value: c.id, label: c.name }))
    );
  }, [supportData.categories]);

  const customerOptions = useMemo(() => {
    return [{ value: "", label: "-- Select --" }].concat(
      supportData.customers.map((c) => ({ value: c.id, label: c.name || c.display_name || c.customer_name }))
    );
  }, [supportData.customers]);

  // submit/update
  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);

    try {
      // sanitize payload: convert empty string -> null, convert ids to numbers
      const payload = {
        title: form.title,
        description: form.description,
        priority: form.priority || null,
        severity: form.severity || null,
        ticket_type_id: safeNumber(form.ticket_type_id),
        category_id: safeNumber(form.category_id),
        customer_id: safeNumber(form.customer_id),
        custom_fields: form.custom_fields || {},
      };

      const resp = await axiosClient.put(`/ticket-management/tickets/${ticket.id}`, payload);

      // success
      // prefer toast but keep minimal: alert (you can replace with toast)
      try { window?.toast?.success?.("Ticket updated"); } catch (_) {}
      onClose();
      if (onUpdated) onUpdated(resp.data);
    } catch (err) {
      console.error("Update failed:", err);
      try { window?.toast?.error?.("Failed to update ticket"); } catch (_) {}
      // provide better message when backend returns structured error
      const msg = err?.response?.data?.message || "Failed to update ticket";
      alert(msg);
    } finally {
      setLoading(false);
    }
  }

  // render custom field definitions (from supportData.custom_fields)
  // supportData.custom_fields is expected in shape: [{id, name, label, field_type, options, required}]
  function renderCustomField(def) {
    const key = def.key || def.id;
    const value = form.custom_fields[key];
    const type = (def.field_type || def.type || "text").toLowerCase();
    const opts = def.options || def.options_list || def.option || [];

    if (type === "textarea") {
      return (
        <div className="tf-field-row" key={key}>
          <label>{def.label || def.name}</label>
          <textarea value={value || ""} onChange={(e) => updateCustomField(key, e.target.value)} />
        </div>
      );
    }

    if (type === "select") {
      const options = [{ value: "", label: "-- Select --" }].concat(
        (opts || []).map((o) => ({ value: o.value ?? o, label: o.label ?? o }))
      );
      return (
        <div key={key}>
          <GlassSelect
            options={options}
            value={value ?? ""}
            onChange={(v) => updateCustomField(key, v)}
            placeholder={def.label || def.name || "Custom Field"}
            searchable
          />
        </div>
      );
    }

    if (type === "checkbox") {
      return (
        <div className="tf-field-row" key={key}>
          <label>
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => updateCustomField(key, e.target.checked)}
            />
            &nbsp; {def.label || def.name}
          </label>
        </div>
      );
    }

    // default text/number
    return (
      <div className="tf-field-row" key={key}>
        <label>{def.label || def.name}</label>
        <input
          type={type === "number" ? "number" : "text"}
          value={value ?? ""}
          onChange={(e) => updateCustomField(key, e.target.value)}
        />
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="tf-editdrawer-overlay">
      <div className="tf-editdrawer-container" style={{ width: 520 }}>
        <div className="tf-editdrawer-header">
          <div>
            <div className="tf-editdrawer-title">Edit Ticket</div>
            <div className="tf-editdrawer-sub">#{ticket?.ticket_number}</div>
          </div>
          <button className="tf-editdrawer-close" onClick={onClose} aria-label="Close">
            <IconClose size={18} />
          </button>
        </div>

        <form className="tf-editdrawer-body" onSubmit={handleSave}>
          {/* Title */}
          <div className="tf-field-row">
            <label>Title</label>
            <input required value={form.title} onChange={(e) => updateField("title", e.target.value)} />
          </div>

          {/* Description */}
          <div className="tf-field-row">
            <label>Description</label>
            <textarea rows={5} value={form.description} onChange={(e) => updateField("description", e.target.value)} />
          </div>

          {/* Priority */}
          <div>
            <GlassSelect
              options={priorityOptions}
              value={form.priority ?? ""}
              onChange={(v) => updateField("priority", v)}
              placeholder="Priority"
            />
          </div>

          {/* Severity */}
          <div>
            <GlassSelect
              options={severityOptions}
              value={form.severity ?? ""}
              onChange={(v) => updateField("severity", v)}
              placeholder="Severity"
            />
          </div>

          {/* Ticket Type */}
          <div>
            <GlassSelect
              options={ticketTypeOptions}
              value={form.ticket_type_id ?? ""}
              onChange={(v) => updateField("ticket_type_id", v)}
              placeholder="Ticket Type"
              searchable
            />
          </div>

          {/* Category */}
          <div>
            <GlassSelect
              options={categoryOptions}
              value={form.category_id ?? ""}
              onChange={(v) => updateField("category_id", v)}
              placeholder="Category"
              searchable
            />
          </div>

          {/* Customer */}
          <div>
            <GlassSelect
              options={customerOptions}
              value={form.customer_id ?? ""}
              onChange={(v) => updateField("customer_id", v)}
              placeholder="Customer"
              searchable
            />
          </div>

          {/* Custom Fields */}
          <div className="tf-editdrawer-customfields">
            <h4>Custom Fields</h4>
            {supportLoading && <div className="tf-muted">Loading custom fields...</div>}
            {!supportLoading && (supportData.custom_fields || []).length === 0 && (
              <div className="tf-muted">No custom fields.</div>
            )}

            {!supportLoading &&
              (supportData.custom_fields || []).map((cf) => renderCustomField(cf))}
          </div>

          {/* Actions */}
          <div className="tf-editdrawer-actions">
            <button type="button" className="tf-btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="tf-btn-primary" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

EditTicketDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  ticket: PropTypes.object.isRequired,
  onUpdated: PropTypes.func,
};

EditTicketDrawer.defaultProps = {
  isOpen: false,
  onUpdated: null,
};
